// Tela da Cobrança na Visão Geral (#6502): João Henrique e Bruna. Abre no dia, sem seletor.
//
// Pergunta da tela: quem eu preciso contatar hoje, e quanto já recuperei?
//   - régua da minha carteira (Lembretes de Cobrança) e promessas de pagamento (carteira);
//   - indicações para trabalhar (Programa de Indicações);
//   - recuperado com contato registrado e a comissão de recuperação prevista, pela regra
//     do plano de bônus (seção 5) — vale se o plano for aprovado;
//   - inadimplência da empresa (regra do bônus, a mesma da Gestão do Financeiro).
//   - #6506: a especialidade de cada um (João: envio dos boletos; Bruna: notificações,
//     documentos e negativações), a régua em dia e os acordos cumpridos.
import React, { useMemo } from 'react';
import { Gift, Handshake, MapPin, Send, Workflow } from 'lucide-react';
import { hojeIsoLocal } from '../../../src/lib/conferenciaFaturamentoDb';
import { formatarValorKpi } from '../../../src/lib/dashboard/kpisDepartamento';
import {
  FAIXAS_RECUPERACAO, promessasDaCarteira, reguaDaCarteira, somarRecuperacao, type Recuperacao,
} from '../../../src/lib/visaoGeral/cobranca';
import { BlocosDoDia } from '../BlocosDoDia';
import { CartaoIndicacoes } from '../CartaoIndicacoes';
import { CartaoEspecialidade, CartoesReguaEAcordos, PainelEspecialidade, useEspecialidade } from '../blocosFase2';
import { PainelReguaDaCarteira } from '../PainelReguaDaCarteira';
import { BotaoAcao, CabecalhoVisaoGeral, CartaoHoje, CartaoIndicador, ListaAtalhos, PainelDetalhe, SecaoVisaoGeral, plural } from '../componentes';
import { usePromessasDaCarteira, useRecuperacaoDoPeriodo, useReguaDaCarteira } from '../useCobranca';
import { useInadimplenciaDaEmpresa } from '../useInadimplenciaDaEmpresa';
import type { PropsTelaVisaoGeral } from '../tipos';

const LISTA = 8;
const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
const reais = (v: number) => formatarValorKpi(v, 'reais');
const reaisCentavos = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const pct = (v: number) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const dataCurta = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');
const vazia = (): Recuperacao => ({ qtd: 0, valor: 0, comissao: 0, porFaixa: FAIXAS_RECUPERACAO.map(() => ({ qtd: 0, valor: 0, comissao: 0 })) });

const TelaCobranca: React.FC<PropsTelaVisaoGeral> = ({ pessoa, souEu, definicao, periodo }) => {
  const hoje = hojeIsoLocal();
  const { inicioMes, fimMes, rotuloMes, primeiroDia, ultimoDia } = useMemo(() => {
    const h = new Date();
    const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return {
      inicioMes: new Date(h.getFullYear(), h.getMonth(), 1),
      fimMes: new Date(h.getFullYear(), h.getMonth() + 1, 1),
      rotuloMes: `${MESES[h.getMonth()]}/${h.getFullYear()}`,
      primeiroDia: iso(new Date(h.getFullYear(), h.getMonth(), 1)),
      ultimoDia: iso(new Date(h.getFullYear(), h.getMonth() + 1, 0)),
    };
  }, []);
  const primeiroNome = (pessoa.full_name || '').split(' ')[0] || 'você';

  // ── dados ──
  const acoes = useReguaDaCarteira(pessoa.id);
  const clientes = usePromessasDaCarteira(pessoa.id);
  const recuperacao = useRecuperacaoDoPeriodo(inicioMes, fimMes);
  const { inadimplencia, meta: metaInad } = useInadimplenciaDaEmpresa();
  const esp = useEspecialidade(pessoa.id, pessoa.full_name ?? null, inicioMes.toISOString(), fimMes.toISOString());

  // ── contas ──
  const regua = useMemo(() => (acoes.dados ? reguaDaCarteira(acoes.dados, pessoa.id, hoje) : null), [acoes.dados, pessoa.id, hoje]);
  const promessas = useMemo(() => (clientes.dados ? promessasDaCarteira(clientes.dados, pessoa.id, hoje) : null), [clientes.dados, pessoa.id, hoje]);
  const minha = recuperacao.dados ? recuperacao.dados.porPessoa.get(pessoa.id) ?? vazia() : null;
  const time = recuperacao.dados ? somarRecuperacao(recuperacao.dados.porPessoa.values()) : null;

  const acoesRapidas = souEu ? <BotaoAcao rotulo="Lembretes de cobrança" icone={Send} para="/financeiro/lembretes-cobranca" /> : null;

  return (
    <div className="space-y-8">
      <CabecalhoVisaoGeral nome={pessoa.full_name} pergunta={definicao.pergunta} periodo={periodo} acoes={acoesRapidas} />

      <BlocosDoDia pessoa={pessoa} perfil="cobranca" />

      <SecaoVisaoGeral titulo="Hoje" subtitulo="régua, promessas e indicações da sua carteira">
        <CartaoEspecialidade e={esp} />
        <CartaoHoje
          valor={regua ? regua.hoje.length : null}
          rotulo="ações da régua para hoje"
          tom="atencao"
          para="/financeiro/lembretes-cobranca"
          dica="Ações D+4, D+10, D+17 e D+24 dos clientes da sua carteira que vencem hoje (dia = vencimento do boleto + N)."
        />
        <CartaoHoje
          valor={regua ? regua.atrasadas.length : null}
          rotulo="ações da régua atrasadas"
          tom={regua && regua.atrasadas.length > 0 ? 'critico' : 'ok'}
          para="/financeiro/lembretes-cobranca"
          dica={regua && regua.maisAntiga ? `Pendentes de dias que já passaram; a mais antiga é de ${dataCurta(regua.maisAntiga)}. Feita ou dispensada com motivo, sai da conta.` : 'Nenhuma ação atrasada.'}
        />
        <CartaoHoje
          valor={promessas ? promessas.hoje.length : null}
          rotulo="promessas de pagamento vencem hoje"
          tom="atencao"
          para="/financeiro/esteira"
          dica={promessas ? `Clientes da sua carteira que combinaram pagar hoje. Mais ${plural(promessas.vencidas.length, 'cliente', 'clientes')} com a data combinada já passada e ainda marcado como "negociado" (confira se pagou) e ${promessas.proximas.length} nos próximos 7 dias.` : undefined}
        />
        <CartaoIndicacoes pessoa={pessoa} />
      </SecaoVisaoGeral>
      {(acoes.erro || clientes.erro) && <p className="-mt-6 text-xs text-red-600 dark:text-red-400">Não foi possível carregar parte da sua carteira.</p>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PainelReguaDaCarteira regua={regua} hoje={hoje} />

        <PainelDetalhe titulo="Promessas de pagamento" acao={{ rotulo: 'Esteira', para: '/financeiro/esteira' }}>
          {!promessas ? (
            <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
          ) : promessas.hoje.length + promessas.vencidas.length + promessas.proximas.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum cliente da sua carteira com pagamento combinado.</p>
          ) : (
            <>
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {[...promessas.hoje.map(c => ({ c, tipo: 'hoje' as const })), ...promessas.vencidas.map(c => ({ c, tipo: 'vencida' as const })), ...promessas.proximas.map(c => ({ c, tipo: 'proxima' as const }))]
                  .slice(0, LISTA).map(({ c, tipo }) => (
                    <li key={c.id} className="flex items-start gap-3 py-2.5">
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{c.cliente_nome || 'Sem nome'}</span>
                        <span className="block text-xs text-gray-400">{Number(c.valor_negociado) > 0 ? `${reais(Number(c.valor_negociado))} combinados` : 'valor não informado'}</span>
                      </span>
                      <span className={`shrink-0 text-right text-xs font-semibold ${tipo === 'vencida' ? 'text-red-600 dark:text-red-400' : tipo === 'hoje' ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                        {tipo === 'hoje' ? 'paga hoje' : tipo === 'vencida' ? `era ${dataCurta(c.data_pagamento_negociado)}` : `para ${dataCurta(c.data_pagamento_negociado)}`}
                        {tipo === 'vencida' && <span className="block font-normal text-gray-500 dark:text-gray-400">conferir se pagou</span>}
                      </span>
                    </li>
                  ))}
              </ul>
              <p className="text-xs text-gray-400 mt-3">
                Clientes da sua carteira marcados como "negociado", pela data combinada. Pagou: marque como pago na Esteira; não pagou: registre o contato.
              </p>
            </>
          )}
        </PainelDetalhe>
      </div>

      <SecaoVisaoGeral titulo="Meu mês" subtitulo={`${rotuloMes}, boletos pagos com atraso depois de um contato de ${primeiroNome}`} colunas={3}>
        <CartaoIndicador
          titulo="Recuperado com contato"
          valor={minha ? reais(minha.valor) : null}
          detalhe={minha && time && recuperacao.dados ? `${plural(minha.qtd, 'boleto pago', 'boletos pagos')} com atraso depois de um contato seu. Time: ${reais(time.valor)}. Pagos com atraso sem nenhum contato registrado: ${reais(recuperacao.dados.semContato.valor)}.` : undefined}
          para="/financeiro/esteira"
          origem="Boleto pago mais de 3 dias depois do vencimento, no mês. Conta para quem registrou o último contato com o cliente (histórico da carteira ou ação da régua concluída) entre o vencimento e o pagamento. Contato que fica só no celular não conta."
        />
        <CartaoIndicador
          titulo="Comissão de recuperação prevista"
          valor={minha ? reaisCentavos(minha.comissao) : null}
          detalhe="Se o plano de bônus for aprovado: de 1,5% a 10% do valor pago, pelo atraso no dia do pagamento. Até 15 dias não rende comissão."
          origem="Faixas da seção 5 do plano de bônus: 16 a 30 dias, 1,5%; 31 a 60, 3%; 61 a 90, 5%; 91 a 180, 7%; mais de 180, 10%. Parcela de acordo entra pelo atraso da própria parcela."
        />
        <CartaoIndicador
          titulo="Inadimplência da empresa"
          valor={inadimplencia ? (inadimplencia.taxa == null ? '—' : pct(inadimplencia.taxa)) : null}
          meta={metaInad}
          detalhe={inadimplencia ? `Boletos vencidos de ${dataCurta(inadimplencia.janela.inicio)} a ${dataCurta(inadimplencia.janela.fim)} ainda em aberto: ${reais(inadimplencia.vencido)}. Estoque vencido: ${reais(inadimplencia.estoque)}.` : undefined}
          origem="Regra do plano de bônus: vencido há mais de 30 dias e em aberto ÷ valor desses boletos, nos 3 meses anteriores. É do time inteiro, com o Faturamento."
        />
        <CartoesReguaEAcordos pessoaId={pessoa.id} inicioIso={primeiroDia} fimIso={ultimoDia} hojeIso={hoje} rotuloMes={rotuloMes} />
      </SecaoVisaoGeral>

      <section className="space-y-3" aria-label="Detalhe">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Detalhe</h2>
        <PainelEspecialidade e={esp} rotuloMes={rotuloMes} />
        <PainelDetalhe titulo="Minha recuperação por faixa de atraso">
          {!minha ? (
            <div className="h-32 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
          ) : (
            <>
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm min-w-[420px]">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 border-b border-gray-100 dark:border-gray-700">
                      <th className="font-medium py-2 px-2">Atraso no pagamento</th>
                      <th className="font-medium py-2 px-2 text-right">Boletos</th>
                      <th className="font-medium py-2 px-2 text-right">Recuperado</th>
                      <th className="font-medium py-2 px-2 text-right">Comissão</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {FAIXAS_RECUPERACAO.map((f, i) => (
                      <tr key={f.rotulo}>
                        <td className="py-2 px-2 text-gray-700 dark:text-gray-200">{f.rotulo} <span className="text-xs text-gray-400">({pct(f.pct)})</span></td>
                        <td className="py-2 px-2 text-right">{minha.porFaixa[i].qtd}</td>
                        <td className="py-2 px-2 text-right">{reais(minha.porFaixa[i].valor)}</td>
                        <td className="py-2 px-2 text-right">{reaisCentavos(minha.porFaixa[i].comissao)}</td>
                      </tr>
                    ))}
                    <tr className="font-semibold">
                      <td className="py-2 px-2">Total</td>
                      <td className="py-2 px-2 text-right">{minha.qtd}</td>
                      <td className="py-2 px-2 text-right">{reais(minha.valor)}</td>
                      <td className="py-2 px-2 text-right">{reaisCentavos(minha.comissao)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Quanto mais antiga a dívida, maior o percentual: o que se paga sozinho nos primeiros dias não rende comissão.
                A comissão só vale se o plano de bônus for aprovado.
              </p>
            </>
          )}
        </PainelDetalhe>
      </section>

      <ListaAtalhos itens={[
        { rotulo: 'Lembretes de cobrança', para: '/financeiro/lembretes-cobranca', icone: Send },
        { rotulo: 'Esteira de cobrança', para: '/financeiro/esteira', icone: Workflow },
        { rotulo: 'Rota de cobrança', para: '/financeiro/rota-cobranca', icone: MapPin },
        { rotulo: 'Parcelamentos', para: '/financeiro/parcelamentos', icone: Handshake },
        { rotulo: 'Indicações', para: '/crm/indicacoes', icone: Gift },
      ]} />
    </div>
  );
};

export default TelaCobranca;
